import { PROPERTY_ENDPOINTS } from "../constants/propertyEndpoints";

export const createPropertyService = (httpClient) => {
  return {
    // 1. List Properties (General View)
    listGeneral: async ({ page_number = 1, page_size = 12, order_by = "DESC" } = {}) => {
      try {
        const response = await httpClient.get(PROPERTY_ENDPOINTS.listGeneral);
        if (response.data && (response.data.properties || response.data.data)) {
          return response.data;
        }
      } catch (e) {
        console.warn("latestList fetch fallback:", e);
      }

      try {
        const fallback = await httpClient.post(PROPERTY_ENDPOINTS.listAdmin, {
          keyboard_search: "",
          page_number,
          page_size,
          order_by,
        });
        return fallback.data;
      } catch (e2) {
        try {
          const fallbackPref = await httpClient.post("/property/list/preferred/v1", {
            keyword_search: "",
            page_number,
            page_size,
            order_by,
          });
          return fallbackPref.data;
        } catch {
          return { properties: [] };
        }
      }
    },

    // 2. List Properties with Filters / Preferred (Partner View)
    listPreferred: async ({
      property_type = "",
      state = "",
      city = "",
      keyboard_search = "",
      keyword_search = "",
      page_number = 1,
      page_size = 12,
      order_by = "DESC",
      is_partner = true,
    } = {}) => {
      const searchKey = keyboard_search || keyword_search || "";
      try {
        const response = await httpClient.post("/property/list/preferred/v1", {
          property_type,
          state,
          city,
          keyword_search: searchKey,
          keyboard_search: searchKey,
          page_number,
          page_size,
          order_by,
          is_partner,
        });
        if (response.data) return response.data;
      } catch (err1) {
        try {
          const response = await httpClient.post(PROPERTY_ENDPOINTS.listPreferred, {
            property_type,
            state,
            city,
            keyboard_search: searchKey,
            keyword_search: searchKey,
            page_number,
            page_size,
            order_by,
            is_partner,
          });
          if (response.data) return response.data;
        } catch (err2) {
          try {
            const fallback = await httpClient.post(PROPERTY_ENDPOINTS.listAdmin, {
              keyboard_search: searchKey,
              keyword_search: searchKey,
              page_number,
              page_size,
              order_by,
            });
            return fallback.data;
          } catch {
            return { properties: [] };
          }
        }
      }
      return { properties: [] };
    },

    // 3. List My Properties
    listMy: async ({
      user_id,
      page_number = 1,
      page_size = 12,
      order_by = "DESC",
    } = {}) => {
      try {
        const response = await httpClient.post(PROPERTY_ENDPOINTS.listMyPreferred, {
          user_id,
          page_number,
          page_size,
          order_by,
        });
        return response.data;
      } catch {
        try {
          const res = await httpClient.post(PROPERTY_ENDPOINTS.listMy, {
            user_id,
            page_number,
            page_size,
            order_by,
          });
          return res.data;
        } catch {
          try {
            const res = await httpClient.post(PROPERTY_ENDPOINTS.listAdmin, {
              page_number,
              page_size,
              order_by,
            });
            return res.data;
          } catch {
            return { properties: [] };
          }
        }
      }
    },

    // 3b. List My Space properties (owner resolved from auth token; no fallback to public lists)
    listMySpace: async ({ page_number = 1, page_size = 12, order_by = "DESC" } = {}) => {
      const response = await httpClient.post(PROPERTY_ENDPOINTS.listMySpace, {
        search_type: "Property",
        page_number,
        page_size,
        order_by,
      });
      return response.data;
    },

    // 4. Get Property Details by ID
    getDetails: async (id) => {
      try {
        const response = await httpClient.get(PROPERTY_ENDPOINTS.details(id));
        return response.data;
      } catch {
        const fallback = await httpClient.get(`/all/property/id/${id}`);
        return fallback.data;
      }
    },

    // 5. Delete Property
    remove: async (id) => {
      const response = await httpClient.delete(PROPERTY_ENDPOINTS.remove(id));
      return response.data;
    },

    // 6. Comments on a Property
    getComments: async (
      userId,
      propertyId,
      { page_number = 1, page_size = 200, order_by = "DESC" } = {}
    ) => {
      try {
        const response = await httpClient.post(
          PROPERTY_ENDPOINTS.comments(userId, propertyId),
          {
            page_number,
            page_size,
            order_by,
          }
        );
        return response.data;
      } catch {
        try {
          const notesRes = await httpClient.post("/property/partner-notes", {
            post_id: 0,
            property_id: propertyId,
            page_number,
            page_size,
            order_by,
          });
          return {
            comments: notesRes.data?.partnerNotesList || [],
          };
        } catch {
          return { comments: [] };
        }
      }
    },

    // 7. Add Comment / Reply
    addComment: async ({ comment, userId, propertyId, parentId = null }) => {
      const payload = {
        comment,
        user: {
          id: userId,
        },
        desired_request: {
          id: propertyId,
        },
      };

      if (parentId) {
        payload.parent_id = parentId;
      }

      try {
        const response = await httpClient.post(
          PROPERTY_ENDPOINTS.addComment,
          payload
        );
        return response.data;
      } catch {
        const noteRes = await httpClient.post("/property/notes/add", {
          note: comment,
          property_id: propertyId,
        });
        return noteRes.data;
      }
    },

    // 8. Edit Comment
    editComment: async ({ id, comment }) => {
      const response = await httpClient.put(PROPERTY_ENDPOINTS.editComment, {
        id,
        comment,
      });
      return response.data;
    },

    // 9. Delete Comment
    deleteComment: async (commentId) => {
      const response = await httpClient.delete(
        PROPERTY_ENDPOINTS.deleteComment(commentId)
      );
      return response.data;
    },

    // Form Dropdown Helpers
    fetchStates: async (countryId = 1) => {
      try {
        const response = await httpClient.post(
          PROPERTY_ENDPOINTS.states(countryId),
          {
            page_number: 1,
            page_size: 100,
            order_by: "ASC",
          }
        );
        return response.data?.states || [];
      } catch {
        return [];
      }
    },

    fetchCities: async (stateId) => {
      try {
        const response = await httpClient.post(
          PROPERTY_ENDPOINTS.cities(stateId),
          {
            page_number: 1,
            page_size: 200,
            order_by: "ASC",
          }
        );
        return response.data?.cities || [];
      } catch {
        return [];
      }
    },

    fetchPropertyTypes: async () => {
      try {
        const response = await httpClient.get(PROPERTY_ENDPOINTS.propertyTypes);
        return response.data?.propertyTypes || [];
      } catch {
        return [];
      }
    },
  };
};
